import { usePartnerConfig, usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { isVoiceTask, useActiveTask, useAgentStore, useCallbackState } from "@/sdk";
import { isNullish } from "@/shared-utils";

import { useIsOutboundEnabled } from "./useIsOutboundEnabled";

/**
 * Whether the call-controls bar has anything to show — and therefore whether the redesigned
 * voice workspace's leftTop panel should be open.
 *
 * Drives the redesigned voice workspace's `leftTopOpen`. Without it the panel opened around an
 * empty `CallControlsManager` (an empty black bar) whenever the partner had call controls off or
 * there was nothing to show. Scoped to the redesigned layout; the legacy topbar keeps its own gate.
 *
 * Uses live-voice-task semantics (excludes terminal `completed`/`cancelled` tasks) so a stale task
 * can't keep the bar open around an empty manager.
 */
export function useShouldShowCallControls(): boolean {
    const { callControlsEnabled } = usePartnerFeatureConfigured();
    const { wrappingAgentActivity } = usePartnerConfig();
    const isOutboundEnabled = useIsOutboundEnabled();
    const activeTask = useActiveTask();
    const callbackState = useCallbackState();
    const agentActivity = useAgentStore((state) => state.activity);

    const hasLiveVoiceTask =
        isVoiceTask(activeTask) && activeTask.status !== "completed" && activeTask.status !== "cancelled";
    const hasCallbackWaiting = Boolean(
        !isNullish(callbackState?.callbackDelay) &&
        callbackState.scheduledFor &&
        callbackState.callbackType === "CallbackNow",
    );
    // Mirror CallControlsManager's WrappingCallControls path (rendered while the agent is in the
    // wrapping activity with no active call): keep the panel open so a taskless wrapping state
    // still shows the bar rather than collapsing to nothing.
    const isWrapping = agentActivity === wrappingAgentActivity;

    return Boolean(callControlsEnabled) && (isOutboundEnabled || hasLiveVoiceTask || hasCallbackWaiting || isWrapping);
}
